import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  Database,
  Download,
  FileQuestion,
  Loader2,
  Search,
  ShieldAlert,
  UserRound,
} from "lucide-react";
import { PageHeader } from "@packages/ui";
import type {
  AuditEventRecord,
  AuditModule,
  PersonalAuditListQuery,
} from "@packages/global";
import { Pagination } from '@packages/ui';
import { accountActivityApi } from "../../services/user";
import { getAuditActionLabel } from "../../utils/auditNotifications";

const PAGE_SIZE = 20;

type ActivityFilter = "all" | "mine" | "attention" | "query" | "exports";

const filterOptions: Array<{ value: ActivityFilter; label: string }> = [
  { value: "all", label: "全部" },
  { value: "mine", label: "我的操作" },
  { value: "attention", label: "需关注" },
  { value: "query", label: "数据质疑" },
  { value: "exports", label: "导出" },
];

function buildQuery(filter: ActivityFilter, page: number, keyword: string): PersonalAuditListQuery {
  const query: PersonalAuditListQuery = {
    page,
    pageSize: PAGE_SIZE,
    keyword: keyword || undefined,
  };
  if (filter === "attention") {
    query.attentionOnly = true;
  }
  if (filter === "mine") {
    query.actorOnly = true;
  }
  if (filter === "query") {
    query.module = "query";
  }
  if (filter === "exports") {
    query.eventType = "export_downloaded";
  }
  return query;
}

function moduleLabel(module: AuditModule | string): string {
  const labels: Record<string, string> = {
    source: "数据源",
    warehouse: "数据仓库",
    research: "研究",
    quality: "质控",
    query: "数据质疑",
    analytics: "统计分析",
    auth: "账号",
    admin: "管理",
  };
  return labels[module] ?? module;
}

function moduleIcon(module: AuditModule | string) {
  if (module === "query") return FileQuestion;
  if (module === "warehouse" || module === "source" || module === "research") return Database;
  if (module === "quality") return ShieldAlert;
  if (module === "auth") return UserRound;
  if (module === "analytics") return Activity;
  return Activity;
}

function moduleVisual(module: AuditModule | string, result: AuditEventRecord["result"]) {
  if (result !== "success") {
    return "bg-danger-soft text-danger";
  }
  if (module === "query") {
    return "bg-primary-soft text-primary-ink";
  }
  if (module === "warehouse" || module === "source" || module === "research") {
    return "bg-success-soft text-success";
  }
  if (module === "quality") {
    return "bg-warning-soft text-warning";
  }
  return "bg-bg-elevated text-text-secondary";
}

function groupActivityTime(time: string): string {
  const date = new Date(time);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "今天";
  if (date.toDateString() === yesterday.toDateString()) return "昨天";
  return "更早";
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function activityText(item: AuditEventRecord): string {
  const actor = item.actorName || item.actorType || "系统";
  const action = getAuditActionLabel(item.action);
  const target = item.targetName || item.targetId || "";
  return `${actor} ${action}${target ? ` · ${target}` : ""}`;
}

const RecentPage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [keywordInput, setKeywordInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<AuditEventRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(
    () => buildQuery(filter, page, keyword),
    [filter, keyword, page],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await accountActivityApi.page(query);
      setRows(data.items);
      setTotal(data.total);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? "load failed");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        eyebrow="My Workspace"
        title="活动记录"
        description="你的账号与平台操作时间线。这里是只读审计记录，不承载通知已读或待办处理。"
        actionsSlot={
          <>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setKeyword(keywordInput.trim());
                setPage(1);
              }}
              className="flex min-h-[38px] w-[300px] items-center gap-2 rounded-xl bg-bg-elevated px-3"
            >
              <Search className="h-[15px] w-[15px] text-text-muted" />
              <input
                value={keywordInput}
                onChange={(event) => setKeywordInput(event.target.value)}
                placeholder="搜索活动..."
                aria-label="搜索活动记录"
                className="min-w-0 flex-1 bg-transparent text-[12.5px] text-text-primary outline-none placeholder:text-text-muted"
              />
              {keywordInput && (
                <button
                  type="button"
                  onClick={() => {
                    setKeywordInput("");
                    setKeyword("");
                    setPage(1);
                  }}
                  className="text-[11.5px] font-medium text-text-muted hover:text-primary-ink"
                >
                  清空
                </button>
              )}
            </form>
            <button
              type="button"
              disabled
              className="inline-flex min-h-[38px] cursor-not-allowed items-center gap-1.5 rounded-[8px] border border-border-default bg-bg-surface px-[13px] text-[12.5px] font-medium text-text-primary opacity-60"
            >
              <Download className="h-3.5 w-3.5" />
              导出记录
            </button>
          </>
        }
      />

      <div className="-mx-4 bg-bg-page px-4 sm:-mx-6 sm:px-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setFilter(option.value);
                  setPage(1);
                }}
                className={`inline-flex items-center rounded-full border px-[13px] py-[6px] text-[12px] transition-colors ${
                  filter === option.value
                    ? "border-action-primary bg-action-primary font-semibold text-white"
                    : "border-border-default bg-bg-surface font-medium text-text-secondary hover:border-action-primary-border hover:bg-bg-elevated"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="rounded-xl border border-danger-border bg-danger-soft px-4 py-3 text-[13px] text-danger">
              加载活动记录失败：{error}
            </div>
          )}

          <section>
            {loading ? (
              <div className="rounded-xl border border-border-subtle bg-bg-surface py-16 text-center text-[13px] text-text-muted">
                <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin opacity-50" />
                加载活动记录中...
              </div>
            ) : rows.length === 0 ? (
              <div className="rounded-xl border border-border-subtle bg-bg-surface py-16 text-center">
                <Activity className="mx-auto h-8 w-8 text-text-muted" />
                <p className="mt-3 text-[14px] font-semibold text-text-primary">暂无活动记录</p>
                <p className="mt-1 text-[12.5px] text-text-muted">当前筛选条件下没有个人审计事件。</p>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="mt-4 inline-flex min-h-8 items-center gap-1.5 rounded-[8px] bg-action-primary px-3 text-[12px] font-semibold text-white hover:bg-action-primary-hover"
                >
                  返回工作台
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {["今天", "昨天", "更早"].map((group) => {
                  const groupRows = rows.filter((item) => groupActivityTime(item.occurredAt) === group);
                  if (groupRows.length === 0) return null;
                  return (
                    <section key={group} className="space-y-2">
                      <div className="font-mono text-[10.5px] text-text-muted">{group}</div>
                      <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
                        {groupRows.map((item, index) => {
                          const Icon = moduleIcon(item.module);
                          return (
                            <article
                              key={item.id}
                              className={`flex items-center gap-[13px] p-[12px_16px] transition-colors hover:bg-bg-elevated ${
                                index > 0 ? "border-t border-border-subtle" : ""
                              }`}
                            >
                              <div className="w-16 shrink-0 font-mono text-[11px] text-text-muted">
                                {formatTime(item.occurredAt)}
                              </div>
                              <div className={`grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[8px] ${moduleVisual(item.module, item.result)}`}>
                                <Icon className="h-[15px] w-[15px]" />
                              </div>
                              <div className="min-w-0 flex-1 truncate text-[12.5px] text-text-primary">
                                {activityText(item)}
                              </div>
                              <div className="hidden shrink-0 items-center gap-[5px] rounded-full bg-bg-elevated px-[10px] py-[3px] text-[10.5px] text-text-secondary md:inline-flex">
                                {moduleLabel(item.module)}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
                <p className="text-center text-[12px] text-text-muted">
                  仅显示你有权限查看的平台活动 · 记录不可修改
                </p>
              </div>
            )}

            {rows.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
                <Pagination
                  page={page}
                  limit={PAGE_SIZE}
                  total={total}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default RecentPage;
